import React, { useMemo, useState } from 'react';
import InfoTooltip from './InfoTooltip';
import RightsInfo from './RightsInfo';

const PONDERI = {
  "Nespecificat": 0,
  "Ușor": 1,
  "Mediu": 2,
  "Accentuat": 3,
  "Grav": 4,
  "Grav cu asistent personal": 5
};

const GRAD_COLORS = {
  "Nespecificat": '#999',
  "Ușor": '#17a2b8',
  "Mediu": '#0056b3',
  "Accentuat": '#e67e22',
  "Grav": '#dc3545',
  "Grav cu asistent personal": '#c0392b'
};

export default function Step3Results({ selectedDiseases, formData, onPrev }) {

  const rezultate = useMemo(() => {
    let severitateMaximaGlobala = 0;
    let gradGlobal = "Nespecificat";
    let documenteLipsaTotal = [];
    let esteIncert = false;

    const rezultateBoli = selectedDiseases.map(boala => {
      let severitateBoala = 0;
      let gradBoala = "Nespecificat";
      let docLipsaBoala = [];
      let missingScales = false;

      const dataForm = formData[boala.id_boala] || {};

      // 1. Calcul Severitate (Scale)
      if (boala.criterii_exactitate.scale_obligatorii) {
        boala.criterii_exactitate.scale_obligatorii.forEach(scala => {
          const valoareSelectata = dataForm.scale?.[scala.id];
          if (valoareSelectata) {
            const gradAdus = scala.valori_permise[valoareSelectata];
            const scor = PONDERI[gradAdus] || 0;
            if (scor > severitateBoala) {
              severitateBoala = scor;
              gradBoala = gradAdus;
            }
          } else {
            missingScales = true;
          }
        });
      }

      // 2. Evaluare Autonomie via Barthel scores
      if (boala.criterii_exactitate.dependenta_ajutor) {
        const barthelScores = dataForm.barthel || {};
        const adlScores = dataForm.adl || {};
        
        const barthelTotal = Object.values(barthelScores).reduce((a, b) => a + b, 0);
        const adlTotal = Object.values(adlScores).reduce((a, b) => a + b, 0);
        
        const barthelAnswered = Object.keys(barthelScores).length;
        const adlAnswered = Object.keys(adlScores).length;
        
        if (barthelAnswered > 0 || adlAnswered > 0) {
          let noulGrad = null;
          
          // Conform regulilor: Barthel 0-4 pct SAU ADL 0-3 pct
          if (barthelTotal <= 4 || adlTotal <= 3) {
            noulGrad = boala.criterii_exactitate.dependenta_ajutor.implica_asistent 
              ? "Grav cu asistent personal" : "Grav";
          } 
          // Barthel 5-9 pct (Dependență severă) SAU ADL 4-5
          else if (barthelTotal <= 9 || adlTotal <= 5) {
            noulGrad = "Grav";
          } 
          // Barthel 10-14 pct (Dependență moderată) SAU ADL 6-7
          else if (barthelTotal <= 14 || adlTotal <= 7) {
            if (severitateBoala < PONDERI["Accentuat"]) {
              noulGrad = "Accentuat";
            }
          }
          
          if (noulGrad && PONDERI[noulGrad] > severitateBoala) {
            severitateBoala = PONDERI[noulGrad];
            gradBoala = noulGrad;
          }
        }
        
        // Fallback for old checkbox logic (if someone skips Barthel)
        if (dataForm.dependenta) {
          let noulGrad = boala.criterii_exactitate.dependenta_ajutor.implica_asistent 
            ? "Grav cu asistent personal" : "Grav";
          if (PONDERI[noulGrad] > severitateBoala) {
            severitateBoala = PONDERI[noulGrad];
            gradBoala = noulGrad;
          }
        }
      }

      // 3. Documente Lipsă
      if (boala.criterii_exactitate.documente_necesare) {
        boala.criterii_exactitate.documente_necesare.forEach(doc => {
          if (!dataForm.documente?.[doc]) {
            docLipsaBoala.push(doc);
            documenteLipsaTotal.push(doc);
          }
        });
      }

      // 4. Istoric
      if (boala.criterii_exactitate.istoric_medical?.necesita_debut && !dataForm.debut) {
        esteIncert = true;
      }

      if (docLipsaBoala.length > 0 || missingScales) {
        esteIncert = true;
      }

      if (severitateBoala > severitateMaximaGlobala) {
        severitateMaximaGlobala = severitateBoala;
        gradGlobal = gradBoala;
      }

      // Compute Barthel summary for display
      const barthelScores = dataForm.barthel || {};
      const adlScores = dataForm.adl || {};
      const iadlScores = dataForm.iadl || {};
      const barthelTotal = Object.values(barthelScores).reduce((a, b) => a + b, 0);
      const adlTotal = Object.values(adlScores).reduce((a, b) => a + b, 0);
      const iadlTotal = Object.values(iadlScores).reduce((a, b) => a + b, 0);

      return {
        nume: boala.nume,
        grad: gradBoala,
        docLipsa: docLipsaBoala,
        incert: missingScales || docLipsaBoala.length > 0,
        barthel: Object.keys(barthelScores).length > 0 ? barthelTotal : null,
        adl: Object.keys(adlScores).length > 0 ? adlTotal : null,
        iadl: Object.keys(iadlScores).length > 0 ? iadlTotal : null,
      };
    });

    return {
      gradFinal: gradGlobal,
      esteIncert,
      documenteLipsaTotal,
      rezultateIndividuale: rezultateBoli
    };

  }, [selectedDiseases, formData]);

  const [showCriteria, setShowCriteria] = useState(false);
  const [showContest, setShowContest] = useState(false);
  const [showRights, setShowRights] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [patientAge, setPatientAge] = useState('');
  const gradColor = GRAD_COLORS[rezultate.gradFinal] || '#333';

  return (
    <>
      <div className="no-print">
        <h2 style={{ color: '#2c3e50', marginBottom: '4px' }}>Pasul 3: Rezultat Pre-încadrare și Dosar</h2>
        
        {/* Date Pacient pentru Print */}
        <div style={{ margin: '16px 0 24px 0', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#495057' }}>Date Pacient (opțional, apar doar pe foaia printată)</h4>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input 
              type="text" 
              placeholder="Nume și Prenume (ex: Popescu Ion)" 
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              className="search-input"
              style={{ flex: 2, padding: '10px 14px' }}
            />
            <input 
              type="text" 
              placeholder="Vârstă (ex: 65 ani)" 
              value={patientAge}
              onChange={(e) => setPatientAge(e.target.value)}
              className="search-input"
              style={{ flex: 1, padding: '10px 14px' }}
            />
          </div>
        </div>
      <p style={{ color: '#7f8c8d', marginTop: 0, marginBottom: '24px', fontSize: '14px' }}>
        Rezultatul estimat pe baza datelor completate.
        <InfoTooltip 
          title="Cum se interpretează rezultatul?" 
          text={`⚠️ ATENȚIE: Acest rezultat este ESTIMATIV și are caracter orientativ.\n\n📌 Regula Handicapului Asociat:\nÎn cazul mai multor afecțiuni, gradul final se stabilește în funcție de severitatea celei PREDOMINANTE (cea mai gravă).\n\n📊 Scorul de Încredere:\n• „Ridicat" = toate documentele sunt bifate și scalele completate\n• „Scăzut/Incert" = lipsesc date sau documente\n\n📅 Valabilitate:\n• Permanent – pentru cazurile grave, cu documentație completă\n• Revizuibil (12-24 luni) – necesită reevaluare periodică\n\n💡 Decizia finală aparține EXCLUSIV Comisiei de Evaluare, care poate solicita documente suplimentare sau consult specializat.`} 
        />
      </p>

      {/* Main Result Box */}
      <div className={`result-box ${rezultate.esteIncert ? 'result-box-warning' : 'result-box-success'}`}>
        <h3 style={{ margin: '0 0 16px 0', color: rezultate.esteIncert ? '#856404' : '#155724', fontSize: '16px' }}>
          {rezultate.esteIncert ? '⚠️' : '✅'} Rezultat Final (Regula Asociată)
        </h3>
        
        <div style={{ marginBottom: '14px' }}>
          <strong style={{ fontSize: '14px', color: '#555' }}>Grad Handicap: </strong> 
          <span style={{ 
            fontWeight: 700, 
            fontSize: '22px',
            color: gradColor,
            display: 'inline-block',
            padding: '2px 12px',
            borderRadius: '6px',
            background: `${gradColor}15`
          }}>
            {rezultate.gradFinal}
          </span>
        </div>

        <div style={{ fontSize: '14px', marginBottom: '10px' }}>
          <strong>Scor de Încredere: </strong>
          {rezultate.esteIncert ? (
            <span style={{ color: '#dc3545', fontWeight: 600 }}>🔴 Scăzut — Date clinice sau documente lipsă</span>
          ) : (
            <span style={{ color: '#28a745', fontWeight: 600 }}>🟢 Ridicat — Dosar Complet</span>
          )}
        </div>

        <div style={{ fontSize: '14px' }}>
          <strong>Valabilitate Estimată: </strong>
          <span>{rezultate.gradFinal === 'Nespecificat' ? '—' : (rezultate.gradFinal.includes('Grav') && !rezultate.esteIncert ? '♾️ Permanent' : '🔄 Revizuibil (12-24 luni)')}</span>
        </div>
      </div>

      {/* Buton Drepturi și Facilități (plasat SUS conform cerinței) */}
      {(rezultate.gradFinal === 'Accentuat' || rezultate.gradFinal.includes('Grav') || rezultate.gradFinal === 'Mediu') && (
        <div style={{ marginBottom: '24px' }} className="no-print">
          <button 
            onClick={() => setShowRights(!showRights)}
            style={{
              width: '100%', padding: '16px', background: 'linear-gradient(135deg, #3182ce, #2b6cb0)', 
              border: 'none', borderRadius: '8px', 
              color: 'white', fontWeight: 700, fontSize: '16px',
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
              cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            📋 VEZI DREPTURI ȘI FACILITĂȚI (LEGEA 448/2006) {showRights ? '▲' : '▼'}
          </button>
          
          {showRights && (
            <div style={{ marginTop: '16px' }}>
              <RightsInfo gradFinal={rezultate.gradFinal} />
            </div>
          )}
        </div>
      )}

      {/* Individual Results */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#2c3e50', borderBottom: '2px solid #eee', paddingBottom: '8px', fontSize: '15px' }}>
          Detaliere: Rezultate Individuale (pe Afecțiuni)
        </h4>
        <p style={{ fontSize: '13px', color: '#666', fontStyle: 'italic', marginBottom: '16px', background: '#fff3cd', padding: '10px', borderRadius: '6px', borderLeft: '4px solid #ffeeba' }}>
          <strong>💡 Explicație Regula Asociată:</strong> Dacă aveți mai multe afecțiuni cu încadrări diferite (de exemplu: un diagnostic Grav și două diagnostice Medii), <strong>Gradul Final va fi întotdeauna cel mai mare dintre ele (Grav)</strong>. Mai jos sunt afișate strict notele obținute separat pentru fiecare diagnostic bifat la Pasul 1, pentru a înțelege cum a calculat sistemul rezultatul final.
        </p>
        {rezultate.rezultateIndividuale.map((r, idx) => {
          const rColor = GRAD_COLORS[r.grad] || '#666';
          return (
            <div key={idx} className="card" style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 500, color: '#333', fontSize: '14px', flex: 1 }}>{r.nume}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ 
                    padding: '4px 12px', 
                    borderRadius: '6px', 
                    fontSize: '13px', 
                    fontWeight: 700,
                    color: 'white',
                    background: rColor
                  }}>
                    {r.grad}
                  </span>
                  {r.incert && <span style={{ fontSize: '12px', color: '#dc3545' }}>⚠️ Incomplet</span>}
                </div>
              </div>
              {(r.barthel !== null || r.adl !== null || r.iadl !== null) && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {r.barthel !== null && (
                    <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}>
                      Barthel: {r.barthel}/20
                    </span>
                  )}
                  {r.adl !== null && (
                    <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }}>
                      ADL: {r.adl}/10
                    </span>
                  )}
                  {r.iadl !== null && (
                    <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}>
                      IADL: {r.iadl}/16
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pașii Următori / Checklist Dosar (Vizibil Atât pe Ecran cât și pe Print) */}
      <div style={{ padding: '20px', background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)', border: '1px solid #ced4da', borderRadius: '12px', marginBottom: '24px' }} className="no-print">
        <h4 style={{ color: '#2b2d42', marginTop: 0, fontSize: '15px' }}>📍 Ce fac mai departe? (Ghidul Pacientului)</h4>
        
        <p style={{ fontSize: '14px', marginBottom: '12px', color: '#495057' }}><strong>Pasul 1: Colectarea Actelor</strong></p>
        <ul style={{ color: '#c62828', paddingLeft: '20px', margin: '0 0 16px 0', fontSize: '14px', listStyleType: 'none' }}>
          <li style={{ marginBottom: '6px', color: '#155724' }}>✅ Referat de la medicul de familie.</li>
          <li style={{ marginBottom: '6px', color: '#155724' }}>✅ Ancheta socială de la primăria de domiciliu.</li>
          {rezultate.documenteLipsaTotal.length > 0 ? (
            rezultate.documenteLipsaTotal.map((doc, idx) => (
              <li key={idx} style={{ marginBottom: '4px', color: '#dc3545', fontWeight: 'bold' }}>❌ LIPSĂ: {doc}</li>
            ))
          ) : (
            <li style={{ color: '#155724' }}>✅ Toate actele medicale specifice bifate! (Scrisoare medicală, analize).</li>
          )}
        </ul>

        <p style={{ fontSize: '14px', marginBottom: '8px', color: '#495057' }}><strong>Pasul 2: Depunerea Dosarului</strong></p>
        <p style={{ fontSize: '13px', margin: '0 0 16px 0', color: '#6c757d' }}>Dosarul completat se depune fizic sau online (dacă e disponibil) la Primăria de domiciliu sau la <strong>DGASPC-ul județean</strong> (Direcția Generală de Asistență Socială și Protecția Copilului).</p>

        <p style={{ fontSize: '14px', marginBottom: '8px', color: '#495057' }}><strong>Pasul 3: Prezentarea la Comisie</strong></p>
        <p style={{ fontSize: '13px', margin: 0, color: '#6c757d' }}>Veți fi programat pentru o evaluare. Vă veți prezenta în persoană având toate <strong>actele în original</strong> și o copie de buletin. La final, certificatul vă va fi trimis prin poștă.</p>
      </div>

      {/* Info Asistent Personal */}
      <div style={{ marginBottom: '24px' }} className="no-print">
        <button 
          onClick={() => setShowCriteria(!showCriteria)}
          style={{
            width: '100%', padding: '14px', background: '#f8f9fa', 
            border: '1px solid #dee2e6', borderRadius: '8px', 
            color: '#0056b3', fontWeight: 600, fontSize: '14px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            cursor: 'pointer', transition: 'all 0.2s ease'
          }}
        >
          <span>ℹ️ Care sunt criteriile de încadrare pentru asistent personal?</span>
          <span>{showCriteria ? '▲' : '▼'}</span>
        </button>
        
        {showCriteria && (
          <div style={{ 
            marginTop: '8px', padding: '20px', background: 'white', 
            border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#4a5568',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <p style={{ fontWeight: 600, color: '#2d3748', marginTop: 0 }}>
              Criteriul fundamental și general pentru încadrarea în gradul de handicap grav cu asistent personal este pierderea totală a capacității de autoservire, autoîngrijire și autogospodărire, persoana fiind complet dependentă de sprijinul permanent al altei persoane pentru activitățile de bază ale vieții de zi cu zi și pentru supraviețuire.
            </p>
            <p style={{ marginBottom: '16px' }}>În funcție de categoria de afecțiuni, comisiile de evaluare folosesc scale de evaluare și criterii clinice specifice pentru a justifica acordarea asistentului personal:</p>
            
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ color: '#3182ce' }}>1. Afecțiuni neurologice, musculare și osteoarticulare:</strong>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '4px' }}><strong>Scorul ADL (Activities of Daily Living):</strong> Încadrarea în stadiul IV (0-3 puncte) indică o dependență totală (imposibilitatea realizării igienei, îmbrăcării, transferului, alimentației) și necesită asistent personal.</li>
                <li style={{ marginBottom: '4px' }}><strong>Imobilizarea la pat sau fotoliu rulant:</strong> Se acordă asistent personal pacienților cu paraplegii/tetraplegii, celor cu stadii terminale de scleroză multiplă, ataxii, distrofii musculare sau boala Parkinson (stadiul V), atunci când persoana nu se poate deplasa, nu se poate transfera singură și prezintă adesea tulburări grave de deglutiție și respirație.</li>
                <li><strong>Amputații majore și anchiloze:</strong> Amputațiile bilaterale sau anchilozele articulațiilor mari care anulează gestualitatea, prehensiunea (prinderea obiectelor) și ortostatismul.</li>
              </ul>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <strong style={{ color: '#3182ce' }}>2. Funcțiile mentale și cognitive:</strong>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '4px' }}><strong>Demențe și Regresie intelectuală:</strong> Se acordă când scorul testului MMSE este {'<='} 5, fiind însoțit de lipsa capacității de autodeterminare, deteriorarea flagrantă a comunicării (incoerență sau mutism), incapacitatea menținerii igienei minime sau existența unui pericol persistent de vătămare (auto/heteroagresivitate).</li>
                <li style={{ marginBottom: '4px' }}><strong>Întârziere mintală:</strong> Nivel profund (QI sub 30), cu o dezvoltare senzitivo-motorie minimă, lipsa autogestionării și a autocontrolului.</li>
                <li><strong>Psihoze majore (ex. Schizofrenie):</strong> Se cuantifică prin scorul GAFS 1-20, aplicabil formelor cu destructurare severă a personalității, rezistente la tratament.</li>
              </ul>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <strong style={{ color: '#3182ce' }}>3. Funcțiile senzoriale:</strong>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Cecitate (Lipsa vederii):</strong> O acuitate vizuală (VAO) {'<='} 1/50 (0,02) (numără degetele la 1 metru) determină acordarea asistentului personal dacă persoana are pierdută total capacitatea de orientare spațială și autoservire.</li>
              </ul>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <strong style={{ color: '#3182ce' }}>4. Boli sistemice, insuficiențe de organ și cancere:</strong>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '4px' }}><strong>Cancere (Neoplazii):</strong> Tumori maligne generalizate (cu metastaze) care determină afectarea severă a autonomiei și dependența de alte persoane la majoritatea activităților zilnice sau imobilizarea la pat.</li>
                <li style={{ marginBottom: '4px' }}><strong>SIDA:</strong> Stadiul clinic C3, marcat de tulburări funcționale progresive și severe ce anulează capacitatea de autoîngrijire.</li>
                <li style={{ marginBottom: '4px' }}><strong>Diabet Zaharat:</strong> În stadiile cu complicații severe pe cel puțin trei organe-țintă (ex. cecitate, insuficiență renală pe dializă, ulcerații/amputații), cu un impact funcțional major care impune dependența permanentă de asistență externă pentru tratament, monitorizare și alimentație.</li>
                <li><strong>Insuficiențe terminale:</strong> Ciroză hepatică clasa Child Pugh C (cu hemoragii și encefalopatie), boală cronică de rinichi stadiul G5 sau insuficiență respiratorie gravă care limitează total autoîngrijirea.</li>
              </ul>
            </div>

            <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />
            
            <h4 style={{ color: '#2c3e50', fontSize: '15px', marginBottom: '12px' }}>Întrebări Frecvente (FAQ) privind Evaluarea Autonomiei</h4>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ display: 'block', color: '#1a202c', marginBottom: '4px' }}>❓ Cum se evaluează autonomia în cazul nevăzătorilor?</strong>
              <p style={{ margin: '0 0 8px 0' }}>Evaluarea autonomiei în cazul nevăzătorilor (persoane cu cecitate absolută sau relativă) se concentrează pe măsurarea capacității acestora de autoservire, autoîngrijire și autogospodărire în viața de zi cu zi. Conform criteriilor și evaluării activităților zilnice (ADL/IADL), autonomia este analizată prin:</p>
              <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }}>
                <li><strong>Orientarea spațială și mobilitatea:</strong> Prezența dificultăților majore de orientare în spațiu. O persoană nevăzătoare are o dependență parțială sau totală pentru a se deplasa în siguranță. Se ia în considerare dacă se deplasează cu un câine-ghid sau dacă necesită însoțitor.</li>
                <li><strong>Capacitatea de autoservire (ADL și IADL):</strong> Măsura în care lipsa vederii limitează activitățile de bază (igienă, îmbrăcare) și cele instrumentale (prepararea hranei fără arsuri, recunoașterea banilor).</li>
                <li><strong>Utilizarea tehnologiei și adaptarea:</strong> Autonomia este direct influențată de capacitatea de a utiliza tehnologie asistivă și de existența unui mediu accesibilizat (ex. pavaj tactil, semafoare sonore).</li>
              </ul>
              <p style={{ margin: 0 }}><strong>Grad de handicap grav CU asistent personal:</strong> persoana are pierdută total capacitatea de autoservire, necesitând sprijin permanent pentru supraviețuire și evitarea accidentelor.<br/>
              <strong>Grad de handicap grav FĂRĂ asistent:</strong> persoana și-a dezvoltat abilități de compensare (este orientată, folosește tehnologie asistivă/câine-ghid) și necesită doar sprijin parțial.</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ display: 'block', color: '#1a202c', marginBottom: '4px' }}>❓ Care este punctajul Barthel minim pentru asistent personal?</strong>
              <p style={{ margin: 0 }}>Scala de incapacitate Barthel utilizează un punctaj de la 0 la 100 de puncte (autonomie completă). Un scor de 60 de puncte semnifică „independența asistată”, iar cel de 75 „cvasiindependența”. Textul legii nu specifică un prag numeric minim strict pe scara Barthel de 100 pentru acordarea asistentului personal, ci folosește un principiu general: asistentul se acordă când persoana are pierdută total capacitatea de autoservire. În schimb, pentru scorul <strong>ADL</strong>, legislația prevede explicit un prag: <strong>0-3 puncte (Stadiul IV)</strong> semnifică dependență totală care necesită asistent personal.</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ display: 'block', color: '#1a202c', marginBottom: '4px' }}>❓ Cum se calculează punctajul ADL pentru a obține asistent personal?</strong>
              <p style={{ margin: '0 0 4px 0' }}>Se însumează punctele obținute la 5 activități de bază (0-2 puncte fiecare):</p>
              <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }}>
                <li><strong>Igienă corporală:</strong> autonomie (2 pct), ajutat parțial (1 pct), dependent (0 pct).</li>
                <li><strong>Îmbrăcare:</strong> autonomie (2 pct), autonomie dar necesita ajutor la incaltare (1 pct), dependent (0 pct).</li>
                <li><strong>Mers la toaletă:</strong> autonomie (2 pct), ajutat (1 pct), ajutat la pat (0 pct).</li>
                <li><strong>Continența sfincteriană:</strong> continent (2 pct), incontinență ocazională (1 pct), incontinent (0 pct).</li>
                <li><strong>Alimentația:</strong> mănâncă singur (2 pct), ajutat tăiat alimente (1 pct), dependent (0 pct).</li>
              </ul>
              <p style={{ margin: 0 }}>Pentru asistent personal, punctajul total trebuie să se încadreze între <strong>0 și 3 puncte (Stadiul IV)</strong>.</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ display: 'block', color: '#1a202c', marginBottom: '4px' }}>❓ Ce activități specifice includ scalele pentru Handicap Grav?</strong>
              <p style={{ margin: '0 0 4px 0' }}><strong>Barthel:</strong> Dependent la Alimentație (0 pct), Mers/Transfer - imobilizat la pat sau în rulant (0-5 pct), Igienă/Îmbrăcat - dependent total (0 pct), Sfinctere - incontinență (0 pct).</p>
              <p style={{ margin: '0 0 4px 0' }}><strong>ADL:</strong> Stadiul IV (0-3 puncte). Toate activitățile sunt evaluate la nivelul de "Dependent" sau "Ajutat la pat".</p>
              <p style={{ margin: 0 }}><strong>IADL:</strong> Există o limitare majoră sau pierderea totală a posibilității de a realiza activitățile cotidiene instrumentale și autogospodărirea.</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ display: 'block', color: '#1a202c', marginBottom: '4px' }}>❓ Care sunt diferențele de punctaj Barthel între gradele de handicap?</strong>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Gradul Mediu:</strong> Stare de „cvasiindependență” (Aprox. 75 puncte). Persoana execută independent majoritatea activităților de bază.</li>
                <li><strong>Gradul Accentuat:</strong> Stare de „independență asistată” (Aprox. 60 puncte). Persoana prezintă limitări parțiale, necesită ajutor pentru a tăia alimentele, a se îmbrăca, a se transfera etc.</li>
                <li><strong>Gradul Grav:</strong> Deși nu e fixat un prag strict sub 60, este caracterizat prin dependență totală. Evaluat de regulă cu "Dependent" (0 puncte) la majoritatea activităților.</li>
              </ul>
            </div>

            <div style={{ marginBottom: '0' }}>
              <strong style={{ display: 'block', color: '#1a202c', marginBottom: '4px' }}>❓ Ce punctaj IADL este necesar pentru gradul accentuat?</strong>
              <p style={{ margin: 0 }}>Nu este specificat un punctaj numeric exact în lege. Decizia se bazează pe constatarea calitativă că evaluarea indică o capacitate funcțională limitată, care permite numai o mică parte din activitățile casnice și autoservirea. Persoana are nevoie de sprijin din partea altei persoane pentru activitățile instrumentale.</p>
            </div>
          </div>
        )}
      </div>

      {/* Info Contestatie */}
      <div style={{ marginBottom: '24px' }} className="no-print">
        <button 
          onClick={() => setShowContest(!showContest)}
          style={{
            width: '100%', padding: '14px', background: '#fff3cd', 
            border: '1px solid #ffeeba', borderRadius: '8px', 
            color: '#856404', fontWeight: 600, fontSize: '14px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            cursor: 'pointer', transition: 'all 0.2s ease'
          }}
        >
          <span>⚖️ Ce fac dacă sunt nemulțumit de decizie? (Ghid Contestație)</span>
          <span>{showContest ? '▲' : '▼'}</span>
        </button>
        
        {showContest && (
          <div style={{ 
            marginTop: '8px', padding: '20px', background: 'white', 
            border: '1px solid #ffeeba', borderRadius: '8px', fontSize: '13px', color: '#4a5568',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <p style={{ fontWeight: 600, color: '#2d3748', marginTop: 0 }}>
              Dacă ești respins sau primești un grad de handicap mai mic decât cel anterior/așteptat, ai la dispoziție o procedură legală clară de contestare, dar și soluții practice pentru a-ți maximiza șansele de reușită.
            </p>

            <h4 style={{ color: '#856404', fontSize: '14px', marginTop: '16px', marginBottom: '8px' }}>Paloșul 1: Pașii Legali de Urmat</h4>
            
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ color: '#2c3e50' }}>Pasul 1: Depunerea contestației administrative</strong>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Termen:</strong> Ai la dispoziție STRICT <strong>30 de zile calendaristice</strong> de la comunicarea deciziei/certificatului.</li>
                <li><strong>Unde:</strong> Dosarul se depune la secretariatul Comisiei județene/locale care a emis certificatul sau direct la registratura ANPDPD din București.</li>
                <li><strong>Formular:</strong> Trebuie să folosești formularul-tip obligatoriu (Anexa 6), completat cu toate elementele cerute, altfel riști respingerea.</li>
              </ul>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <strong style={{ color: '#2c3e50' }}>Pasul 2: Evaluarea de către Comisia Superioară</strong>
              <p style={{ margin: '4px 0 0 0' }}>Comisia Superioară va analiza dosarul și va emite o decizie de admitere sau respingere.<br/>
              ⚠️ <strong>Risc major:</strong> Noul grad obținut devine <strong>definitiv și obligatoriu</strong>, chiar dacă este, din greșeală, mai mic decât cel din certificatul contestat inițial!</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: '#2c3e50' }}>Pasul 3: Acțiunea în instanță (Contencios Administrativ)</strong>
              <p style={{ margin: '4px 0 0 0' }}>Dacă ești nemulțumit și de răspunsul Comisiei Superioare, următoarea cale este instanța (Tribunal) pe Legea 554/2004.<br/>
              🔹 Avantaj: Cererile sunt <strong>scutite de taxa judiciară de timbru!</strong><br/>
              🔹 Termen: Aproximativ 6 luni de la răspunsul Comisiei, dar se recomandă consultarea unui avocat pentru termenele fixate de procedura tribunalului local.</p>
            </div>

            <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />

            <h4 style={{ color: '#155724', fontSize: '14px', marginTop: '16px', marginBottom: '8px' }}>Ghid Strategic: Cum să câștigi Comisia Superioară?</h4>
            <p style={{ margin: '0 0 12px 0' }}>Simplul fapt de a scrie în formular că "nu ești de acord" nu este suficient. Dosarul trimis automat este exact același cu cel care a fost respins inițial. Varianta cea mai corectă este aducerea de dovezi proaspete:</p>

            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '6px' }}><strong>Rapoarte noi și opinii suplimentare:</strong> Obține referate recente de la specialiști care explică clar impactul afecțiunilor asupra capacității tale de a te îngriji, a funcționa și a lucra.</li>
              <li style={{ marginBottom: '6px' }}><strong>Investigații recente:</strong> Agață de contestație analize, audiograme, teste RMN, teste psihologice (scale noi) ce dovedesc severitatea la ziua respectivă.</li>
              <li style={{ marginBottom: '6px' }}><strong>Atenție la documentele învechite:</strong> Documentele medicale mari, pentru a fi relevante, trebuie să aibă de regulă o vechime de cel mult 6-12 luni. Fă analize actualizate.</li>
              <li><strong>Răspunde prompt Comisiei:</strong> Dacă la re-evaluare comisia mai solicită un act, furnizează-l imediat. Fără el nu se poartă o evaluare corectă.</li>
            </ul>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
        <button onClick={onPrev} className="btn btn-secondary">
          ← Înapoi (Modifică)
        </button>
        <button onClick={() => window.print()} className="btn btn-print">
          🖨️ Printează Referatul
        </button>
      </div>
      </div>

      {/* PRINT ONLY LAYOUT */}
      <div className="print-only" style={{ padding: '0', maxWidth: '800px', margin: '0 auto', color: '#000', fontFamily: 'serif' }}>
        
        <div style={{ borderBottom: '2px solid #000', paddingBottom: '10px', marginBottom: '20px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '20px', margin: '0 0 8px 0', textTransform: 'uppercase' }}>REFERAT DE PRE-EVALUARE PENTRU<br/>ÎNCADRAREA ÎN GRAD DE HANDICAP</h1>
          <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
            Generat la data: <strong>{new Date().toLocaleDateString('ro-RO')}</strong>
          </p>
        </div>

        {patientName && (
          <div style={{ marginBottom: '20px', fontSize: '16px' }}>
            <p><strong>Nume și Prenume:</strong> {patientName}</p>
            {patientAge && <p><strong>Vârstă:</strong> {patientAge}</p>}
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', borderBottom: '1px solid #ccc', paddingBottom: '4px', textTransform: 'uppercase' }}>1. Afecțiuni Medicale Evaluate</h3>
          <ul style={{ paddingLeft: '20px', fontSize: '15px', lineHeight: '1.5' }}>
            {rezultate.rezultateIndividuale.map((r, idx) => (
              <li key={idx} style={{ marginBottom: '8px' }}>
                <strong>{r.nume}</strong>
                <br/>
                <em>Severitate identificată: </em> {r.grad}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', borderBottom: '1px solid #ccc', paddingBottom: '4px', textTransform: 'uppercase' }}>2. Evaluarea Autonomiei</h3>
          <div style={{ padding: '10px 15px', border: '1px solid #000', backgroundColor: '#f9f9f9' }}>
            <p style={{ margin: '0 0 8px 0' }}>S-a efectuat maparea scorurilor privind capacitatea de autoîngrijire:</p>
            <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '14px' }}>
              {rezultate.rezultateIndividuale.map((r, idx) => {
                if (r.barthel !== null || r.adl !== null || r.iadl !== null) {
                  return (
                    <li key={idx}>
                      Afectare: {r.nume.substring(0, 50)}...
                      {r.barthel !== null ? ` — Barthel: ${r.barthel}/20` : ''}
                      {r.adl !== null ? ` — ADL: ${r.adl}/10` : ''}
                      {r.iadl !== null ? ` — IADL: ${r.iadl}/16` : ''}
                    </li>
                  )
                }
                return null;
              })}
            </ul>
             <p style={{ margin: '10px 0 0 0', fontWeight: 'bold' }}>Grad global estimat direct din scale funcționale: {rezultate.gradFinal}</p>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', borderBottom: '1px solid #ccc', paddingBottom: '4px', textTransform: 'uppercase' }}>3. Checklist Dosar Fizic</h3>
          <p style={{ fontSize: '14px', marginBottom: '10px' }}>Următoarele documente constituie baza pre-evaluării și trebuie atașate la dosarul care va fi depus (verificați și bifați):</p>
          <ul style={{ listStyleType: 'none', padding: 0 }}>
            {/* Arătăm inclusiv cele bifate și nebifate vizual ca checklist pntru user */}
            <li style={{ marginBottom: '6px' }}><span style={{ border: '1px solid #000', display: 'inline-block', width: '12px', height: '12px', marginRight: '8px' }}></span>Referat medic de familie</li>
            <li style={{ marginBottom: '6px' }}><span style={{ border: '1px solid #000', display: 'inline-block', width: '12px', height: '12px', marginRight: '8px' }}></span>Ancheta socială de la primărie</li>
            {rezultate.documenteLipsaTotal.map((doc, idx) => (
              <li key={idx} style={{ marginBottom: '6px' }}>
                <span style={{ border: '1px solid #000', display: 'inline-block', width: '12px', height: '12px', marginRight: '8px', position: 'relative', top: '1px' }}></span>
                <strong>LIPSĂ:</strong> {doc}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ marginBottom: '30px', marginTop: '30px', border: '2px solid #000', padding: '15px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '18px', margin: '0 0 10px 0' }}>GRAD DE HANDICAP ESTIMAT: <span style={{ borderBottom: '1px solid #000' }}>{rezultate.gradFinal}</span></h2>
          {rezultate.esteIncert && <p style={{ fontSize: '13px', margin: 0, fontWeight: 'bold' }}>* Estimare parțială datorită documentelor și/sau scalelor incomplete.</p>}
        </div>

        <p style={{ fontSize: '11px', textAlign: 'justify', fontStyle: 'italic', color: '#555' }}>
          Acest document a fost generat printr-un sistem informatic de tip ghid medico-psihosocial și are strict un rol orientativ / de sprijin pentru pacient. Încadrarea oficială și finală aparține, prin decizie motivată individualizată, exclusiv Comisiei de Evaluare a Persoanelor Adulte cu Handicap, conform criteriilor în vigoare din legislația națională.
        </p>

        <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '5px' }}>Semnătură Pacient/Aparținător</div>
          <div style={{ borderTop: '1px solid #000', width: '250px', textAlign: 'center', paddingTop: '5px' }}>Semnătură Asistent Social / Evaluator</div>
        </div>

      </div>
    </>
  );
}
